import { useEffect, useState } from "react";
import { BadgeCheck, Ban, Building2, CalendarDays, CheckCircle2, CreditCard, Eraser, Eye, Trash2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/ui/date-input";
import { toast } from "sonner";

const PLAN_LABELS = { trial: "تجريبي", basic: "الأساسية", premium: "البريميوم", semester: "الشاملة (فصل)", year: "الشاملة (عام)" };

export default function AdminTab() {
  const [users, setUsers] = useState(null);
  const [cleanDays, setCleanDays] = useState(7);
  const [cleaning, setCleaning] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const toggleSelect = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const selectable = (users || []).filter((u) => u.role !== "admin");
  const allSelected = selectable.length > 0 && selectable.every((u) => selected.has(u.id));

  const cleanupSelected = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    const names = users.filter((u) => selected.has(u.id)).map((u) => u.email).slice(0, 10).join("\n");
    if (!window.confirm(`سيتم حذف ${ids.length} حساباً محدداً مع جميع بياناتهم (موظفون، سجلات دوام، مساءلات):\n\n${names}\n\nمتابعة؟`)) return;
    if (!window.confirm("تأكيد نهائي: لا يمكن التراجع عن هذا الحذف. هل أنت متأكد؟")) return;
    setCleaning(true);
    try {
      const res = await api.delete("/admin/users", { data: { ids } });
      toast.success(`تم حذف ${res.data.users} حساباً و ${res.data.employees || 0} موظفاً و ${res.data.attendance || 0} سجل دوام`);
      setSelected(new Set());
      load();
    } catch { toast.error("فشل تنظيف الحسابات المحددة"); } finally { setCleaning(false); }
  };

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);

  const [planDates, setPlanDates] = useState(null);
  const [savingDates, setSavingDates] = useState(false);
  useEffect(() => { api.get("/admin/plan-dates").then((r) => setPlanDates(r.data)).catch(() => {}); }, []);

  const savePlanDates = async () => {
    setSavingDates(true);
    try {
      await api.put("/admin/plan-dates", planDates);
      toast.success("تم حفظ التواريخ وتطبيقها على المشتركين المفعّلين");
      load();
    } catch { toast.error("فشل حفظ التواريخ"); } finally { setSavingDates(false); }
  };

  const [moyasarCheck, setMoyasarCheck] = useState(null);
  const [checkingMoyasar, setCheckingMoyasar] = useState(false);

  const checkMoyasar = async () => {
    setCheckingMoyasar(true);
    setMoyasarCheck(null);
    try {
      const r = await api.get("/admin/moyasar/health");
      setMoyasarCheck(r.data);
    } catch (e) {
      setMoyasarCheck({ success: false, message: e.response?.data?.detail || "فشل تنفيذ الفحص" });
    } finally { setCheckingMoyasar(false); }
  };

  const [bankReqs, setBankReqs] = useState([]);
  const loadReqs = () => api.get("/admin/payment-requests").then((r) => setBankReqs(r.data)).catch(() => setBankReqs([]));
  useEffect(() => { loadReqs(); }, []);

  const viewReceipt = async (req) => {
    try {
      const r = await api.get(`/admin/payment-requests/${req.id}/receipt`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("تعذر فتح الإيصال"); }
  };

  const reviewRequest = async (req, action) => {
    try {
      await api.post(`/admin/payment-requests/${req.id}/${action}`);
      toast.success(action === "approve" ? "تم تفعيل اشتراك المستخدم" : "تم رفض الطلب");
      loadReqs();
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "فشل تنفيذ العملية"); }
  };

  const cleanupTestAccounts = async () => {
    try {
      const preview = await api.get(`/admin/test-accounts/preview?days=${cleanDays}`);
      const { count, emails } = preview.data;
      if (count === 0) { toast.info("لا توجد حسابات حديثة ضمن هذه الفترة"); return; }
      const list = emails.slice(0, 10).join("\n") + (count > 10 ? `\n... و${count - 10} حساباً آخر` : "");
      if (!window.confirm(`سيتم حذف ${count} حساباً مسجلاً خلال آخر ${cleanDays} يوم مع جميع بياناتهم (موظفون، سجلات دوام، مساءلات):\n\n${list}\n\nمتابعة؟`)) return;
      if (!window.confirm("تأكيد نهائي: لا يمكن التراجع عن هذا الحذف. هل أنت متأكد؟")) return;
      setCleaning(true);
      const res = await api.delete(`/admin/test-accounts?days=${cleanDays}`);
      toast.success(`تم حذف ${res.data.users} حساباً و ${res.data.employees || 0} موظفاً و ${res.data.attendance || 0} سجل دوام`);
      load();
    } catch { toast.error("فشل تنظيف الحسابات"); } finally { setCleaning(false); }
  };

  const activate = async (id, plan) => {
    try {
      await api.put(`/admin/users/${id}/subscription`, { plan });
      toast.success("تم تفعيل الاشتراك");
      load();
    } catch { toast.error("فشل التفعيل"); }
  };

  const disable = async (id) => {
    if (!window.confirm("تعطيل اشتراك هذا المستخدم؟ لن يتمكن من استخدام المنصة حتى إعادة التفعيل.")) return;
    try {
      await api.put(`/admin/users/${id}/subscription/disable`);
      toast.success("تم تعطيل الاشتراك");
      load();
    } catch { toast.error("فشل التعطيل"); }
  };

  const remove = async (id) => {
    if (!window.confirm("حذف اشتراك هذا المستخدم نهائياً؟ ستُزال الباقة وتواريخها ويبقى الحساب بدون اشتراك.")) return;
    try {
      await api.delete(`/admin/users/${id}/subscription`);
      toast.success("تم حذف الاشتراك");
      load();
    } catch { toast.error("فشل الحذف"); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border shadow-sm p-4" data-testid="plan-dates-card">
        <p className="font-bold text-sm text-emerald-950 mb-1 flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-emerald-800" /> تواريخ انتهاء الخطط
        </p>
        <p className="text-xs text-slate-500 mb-3 leading-6">
          تُطبَّق على التفعيلات الجديدة وعلى الدفعات الإلكترونية، وتُحدَّث تلقائياً للمشتركين المفعّلين الحاليين في نفس الباقة.
        </p>
        {planDates ? (
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <label className="text-xs text-slate-500 block">نهاية الفصل الدراسي</label>
              <DateInput type="date" value={planDates.semester_end}
                onChange={(e) => setPlanDates({ ...planDates, semester_end: e.target.value })}
                className="h-9 w-44" data-testid="input-semester-end" />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-slate-500 block">نهاية العام الدراسي</label>
              <DateInput type="date" value={planDates.year_end}
                onChange={(e) => setPlanDates({ ...planDates, year_end: e.target.value })}
                className="h-9 w-44" data-testid="input-year-end" />
            </div>
            <Button size="sm" onClick={savePlanDates} disabled={savingDates}
              data-testid="btn-save-plan-dates"
              className="h-9 bg-emerald-900 hover:bg-emerald-950 text-white">
              {savingDates ? "جارٍ الحفظ..." : "حفظ التواريخ"}
            </Button>
          </div>
        ) : <p className="text-xs text-slate-400">جارٍ التحميل...</p>}
      </div>
      <div className="bg-white rounded-2xl border shadow-sm p-4" data-testid="moyasar-check-card">
        <p className="font-bold text-sm text-emerald-950 mb-1 flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-emerald-800" /> فحص بوابة الدفع (Moyasar)
        </p>
        <p className="text-xs text-slate-500 mb-3 leading-6">
          فحص قراءة فقط يتحقق من تهيئة المفاتيح وصحة الاتصال بخوادم Moyasar — لا يُنشئ أي عملية دفع.
        </p>
        <Button size="sm" variant="outline" onClick={checkMoyasar} disabled={checkingMoyasar}
          data-testid="btn-check-moyasar" className="h-9 gap-1.5">
          {checkingMoyasar ? "جارٍ الفحص..." : "فحص الاتصال الآن"}
        </Button>
        {moyasarCheck && (
          <div data-testid="moyasar-check-result"
            className={`mt-3 rounded-xl border px-4 py-3 ${
              moyasarCheck.success
                ? "bg-emerald-50 border-emerald-200"
                : "bg-rose-50 border-rose-200"}`}>
            <p className={`flex items-center gap-2 text-sm font-bold ${
              moyasarCheck.success ? "text-emerald-800" : "text-rose-700"}`}>
              {moyasarCheck.success
                ? <CheckCircle2 className="w-4 h-4 shrink-0" />
                : <XCircle className="w-4 h-4 shrink-0" />}
              {moyasarCheck.message}
            </p>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500">
              <span>المفتاح العلني: {moyasarCheck.publishable_key_configured ? (moyasarCheck.publishable_key_mode === "live" ? "حقيقي" : "تجريبي") : "غير مهيأ"}</span>
              <span>المفتاح السري: {moyasarCheck.secret_key_configured ? (moyasarCheck.secret_key_mode === "live" ? "حقيقي" : "تجريبي") : "غير مهيأ"}</span>
              {moyasarCheck.http_status && <span dir="ltr">HTTP {moyasarCheck.http_status}</span>}
            </div>
          </div>
        )}
      </div>
      <div className="bg-white rounded-2xl border shadow-sm p-4" data-testid="bank-requests-card">
        <p className="font-bold text-sm text-emerald-950 mb-1 flex items-center gap-2">
          <Building2 className="w-4 h-4 text-emerald-800" /> طلبات التحويل البنكي
          {bankReqs.length > 0 && (
            <span className="text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300 rounded-full px-2 py-0.5"
              data-testid="bank-requests-count">{bankReqs.length} بانتظار المراجعة</span>
          )}
        </p>
        {bankReqs.length === 0 ? (
          <p className="text-xs text-slate-400" data-testid="bank-requests-empty">لا توجد طلبات تحويل بنكي بانتظار المراجعة</p>
        ) : (
          <div className="space-y-2 mt-2">
            {bankReqs.map((req) => (
              <div key={req.id} data-testid={`bank-request-${req.id}`}
                className="flex flex-wrap items-center gap-2 border rounded-xl px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">{req.name}</p>
                  <p className="text-[11px] text-slate-400 font-mono" dir="ltr">{req.email}</p>
                </div>
                <span className="text-[11px] font-bold text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                  {PLAN_LABELS[req.plan] || req.plan} — {req.amount} ريال
                </span>
                <span className="text-[10px] text-slate-400" dir="ltr">{(req.created_at || "").slice(0, 10)}</span>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => viewReceipt(req)}
                    data-testid={`btn-view-receipt-${req.id}`} className="h-8 text-[11px] gap-1">
                    <Eye className="w-3.5 h-3.5" /> الإيصال
                  </Button>
                  <Button size="sm" onClick={() => reviewRequest(req, "approve")}
                    data-testid={`btn-approve-request-${req.id}`}
                    className="h-8 text-[11px] gap-1 bg-emerald-900 hover:bg-emerald-950 text-white">
                    <CheckCircle2 className="w-3.5 h-3.5" /> تفعيل
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => reviewRequest(req, "reject")}
                    data-testid={`btn-reject-request-${req.id}`}
                    className="h-8 text-[11px] gap-1 text-rose-700 border-rose-200 hover:bg-rose-50">
                    <Ban className="w-3.5 h-3.5" /> رفض
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="bg-white rounded-2xl border shadow-sm overflow-x-auto" data-testid="admin-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-slate-50 border-b">
        <p className="text-xs text-slate-500">إدارة اشتراكات المستخدمين وتنظيف الحسابات التجريبية</p>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500 whitespace-nowrap">المسجّلون خلال آخر</label>
          <Input type="number" min={1} max={90} value={cleanDays} dir="ltr"
            onChange={(e) => setCleanDays(Math.max(1, Math.min(90, Number(e.target.value) || 1)))}
            className="h-8 w-16 text-center" data-testid="input-cleanup-days" />
          <span className="text-xs text-slate-500">يوم</span>
          <Button size="sm" variant="outline" onClick={cleanupTestAccounts} disabled={cleaning}
            data-testid="btn-cleanup-test-accounts"
            className="h-8 text-xs gap-1.5 text-rose-700 border-rose-300 hover:bg-rose-50">
            <Eraser className="w-3.5 h-3.5" /> {cleaning ? "جارٍ التنظيف..." : "تنظيف الحسابات التجريبية"}
          </Button>
          {selected.size > 0 && (
            <Button size="sm" variant="outline" onClick={cleanupSelected} disabled={cleaning}
              data-testid="btn-cleanup-selected"
              className="h-8 text-xs gap-1.5 text-white bg-rose-600 border-rose-600 hover:bg-rose-700">
              <Trash2 className="w-3.5 h-3.5" /> تنظيف المحدد ({selected.size})
            </Button>
          )}
        </div>
      </div>
      <table className="w-full text-sm" data-testid="table-admin-users">
        <thead>
          <tr className="bg-slate-50 text-slate-500 text-xs">
            <th className="px-3 py-3 w-10">
              <Checkbox checked={allSelected}
                onCheckedChange={(v) => setSelected(v ? new Set(selectable.map((u) => u.id)) : new Set())}
                data-testid="check-select-all-users" aria-label="تحديد كل الحسابات" />
            </th>
            {["الاسم", "البريد الإلكتروني", "رقم الهاتف", "الباقة", "الحالة", "نهاية الاشتراك", "تاريخ التسجيل", "الإجراءات"].map((h) => (
              <th key={h} className="px-4 py-3 font-semibold text-right whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {users === null && <tr><td colSpan={9} className="text-center py-10 text-slate-400">جارٍ التحميل...</td></tr>}
          {users?.map((u) => (
            <tr key={u.id} className="border-t hover:bg-slate-50/60">
              <td className="px-3 py-2.5 w-10">
                <Checkbox checked={selected.has(u.id)} disabled={u.role === "admin"}
                  onCheckedChange={() => toggleSelect(u.id)}
                  data-testid={`check-select-user-${u.id}`} aria-label={`تحديد ${u.name}`} />
              </td>
              <td className="px-4 py-2.5 font-semibold whitespace-nowrap">
                {u.name} {u.role === "admin" && <span className="text-[10px] text-amber-700">(مدير)</span>}
              </td>
              <td className="px-4 py-2.5 text-slate-500" dir="ltr">{u.email}</td>
              <td className="px-4 py-2.5 text-slate-500 font-mono text-xs" dir="ltr"
                data-testid={`user-phone-${u.id}`}>{u.phone || "—"}</td>
              <td className="px-4 py-2.5">{PLAN_LABELS[u.plan] || "—"}</td>
              <td className="px-4 py-2.5">
                <span className={`text-[11px] font-semibold border rounded-full px-2.5 py-0.5 ${
                  u.plan_status === "active"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : u.plan_status === "disabled" || u.plan_status === "expired"
                      ? "bg-rose-50 text-rose-700 border-rose-200"
                      : "bg-amber-50 text-amber-700 border-amber-200"}`}
                  data-testid={`sub-status-${u.id}`}>
                  {u.plan_status === "active" ? "مفعّل"
                    : u.plan_status === "disabled" ? "معطّل"
                      : u.plan_status === "expired" ? "منتهي"
                        : u.plan_status === "pending_payment" ? "بانتظار الدفع" : "بدون اشتراك"}
                </span>
              </td>
              <td className="px-4 py-2.5 font-mono text-xs" dir="ltr">
                {u.subscription_end ? u.subscription_end.slice(0, 10) : "—"}
              </td>
              <td className="px-4 py-2.5 font-mono text-xs" dir="ltr">
                {u.created_at ? u.created_at.slice(0, 10) : "—"}
              </td>
              <td className="px-4 py-2.5">
                {u.role !== "admin" && (
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => activate(u.id, "basic")}
                      data-testid={`btn-activate-basic-${u.id}`} className="h-7 text-[11px] gap-1">
                      <BadgeCheck className="w-3 h-3" /> أساسية
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => activate(u.id, "premium")}
                      data-testid={`btn-activate-premium-${u.id}`} className="h-7 text-[11px] gap-1">
                      <BadgeCheck className="w-3 h-3" /> بريميوم
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => activate(u.id, "semester")}
                      data-testid={`btn-activate-semester-${u.id}`} className="h-7 text-[11px] gap-1">
                      <BadgeCheck className="w-3 h-3" /> شاملة فصل
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => activate(u.id, "year")}
                      data-testid={`btn-activate-year-${u.id}`} className="h-7 text-[11px] gap-1">
                      <BadgeCheck className="w-3 h-3" /> شاملة عام
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => disable(u.id)}
                      data-testid={`btn-disable-sub-${u.id}`} className="h-7 text-[11px] gap-1 text-amber-700 border-amber-300 hover:bg-amber-50">
                      <Ban className="w-3 h-3" /> تعطيل
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => remove(u.id)}
                      data-testid={`btn-delete-sub-${u.id}`} className="h-7 text-[11px] gap-1 text-rose-700 border-rose-300 hover:bg-rose-50">
                      <Trash2 className="w-3 h-3" /> حذف
                    </Button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}
